import React, { useState, useMemo } from "react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  LayoutGrid, FileText, BarChart3, TrendingUp, Receipt, PhoneCall,
  Gauge, Scale, Banknote, Sparkles, Send, X, ShieldCheck, AlertTriangle,
  RefreshCw, Inbox, Search,
} from "lucide-react";

/* ----------------------------------------------------------------------------
 * Radha — Module 1: Collections Copilot dashboard (self-contained preview)
 * In production this splits into pages/Collections.tsx + components/* + the
 * Zustand store + TanStack Query hooks (provided as separate files). Here it
 * is one renderable file seeded with the same data as 001_collections.sql.
 * -------------------------------------------------------------------------- */

const C = {
  primary: "#1B54F8", success: "#00A87A", warning: "#C8861A", danger: "#D63B2F",
  ai: "#6E44D4", aiBg: "#F5F3FF", bg: "#F3F2EE", surface: "#FFFFFF",
  border: "#E4E3DE", text: "#08090C", muted: "#7A7975",
};
const mono = "'DM Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const sans = "'Inter', system-ui, -apple-system, sans-serif";

/* Indian currency: >=1Cr -> ₹X.XXCr, >=1L -> ₹XXL, else grouped */
function formatINR(n) {
  if (n == null || isNaN(n)) return "—";
  const a = Math.abs(n), s = n < 0 ? "-" : "";
  if (a >= 1e7) return `${s}₹${(a / 1e7).toFixed(2)}Cr`;
  if (a >= 1e5) return `${s}₹${Math.round(a / 1e5)}L`;
  return `${s}₹${a.toLocaleString("en-IN")}`;
}

const BAND = {
  low: C.success, medium: C.warning, high: C.danger, critical: C.danger,
};

/* Seed: the 8 accounts from 001_collections.sql */
const ACCOUNTS = [
  { name: "Reliance Industries Ltd", state: "Maharashtra", out: 12500000, dpd: 18, stage: "early", risk: 22.5, band: "low", rec: 0.92, msme: false, agent: "Sneha Patel" },
  { name: "Tata Consultancy Services", state: "Maharashtra", out: 8400000, dpd: 41, stage: "mid", risk: 38, band: "medium", rec: 0.81, msme: false, agent: "Sneha Patel" },
  { name: "Infosys Ltd", state: "Karnataka", out: 6200000, dpd: 67, stage: "late", risk: 54, band: "high", rec: 0.64, msme: false, agent: "Kavitha Rajan" },
  { name: "Shakti Precision Components", state: "Maharashtra", out: 3100000, dpd: 52, stage: "mid", risk: 71, band: "high", rec: 0.48, msme: true, agent: "Kavitha Rajan" },
  { name: "Ganga Engineering Works", state: "Gujarat", out: 1850000, dpd: 96, stage: "severe", risk: 84, band: "critical", rec: 0.29, msme: true, agent: "Suresh Menon" },
  { name: "Mahindra & Mahindra", state: "Maharashtra", out: 9700000, dpd: 9, stage: "current", risk: 15, band: "low", rec: 0.95, msme: false, agent: "Sneha Patel" },
  { name: "Wipro Ltd", state: "Karnataka", out: 4400000, dpd: 73, stage: "late", risk: 58, band: "high", rec: 0.55, msme: false, agent: "Kavitha Rajan" },
  { name: "L&T Finance Holdings", state: "Maharashtra", out: 15600000, dpd: 31, stage: "mid", risk: 33, band: "medium", rec: 0.78, msme: false, agent: "Suresh Menon" },
];

const RECOVERY_TREND = [
  { m: "Jan", rate: 61 }, { m: "Feb", rate: 64 }, { m: "Mar", rate: 67 },
  { m: "Apr", rate: 71 }, { m: "May", rate: 74 }, { m: "Jun", rate: 78 },
];

const MODULES = [
  { code: "M1", name: "Collections Copilot", icon: PhoneCall, active: true },
  { code: "M2", name: "Dispute Resolution", icon: Scale },
  { code: "M3", name: "Contract MIS", icon: FileText },
  { code: "M4", name: "Revenue Forecast", icon: TrendingUp },
  { code: "M5", name: "Invoicing Engine", icon: Receipt },
  { code: "M6", name: "Payment Reminders", icon: Send },
  { code: "M7", name: "Cash Application", icon: Banknote },
  { code: "M8", name: "Cash Flow Forecast", icon: Gauge },
  { code: "M9", name: "CRM Pipeline", icon: LayoutGrid },
];

function Badge({ children, color, bg }) {
  return (
    <span style={{
      fontFamily: mono, fontSize: 9, letterSpacing: 0.5, textTransform: "uppercase",
      color, background: bg, border: `0.5px solid ${color}33`,
      borderRadius: 20, padding: "3px 8px", whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

function KPICard({ label, value, sub, accent }) {
  return (
    <div style={{
      background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12,
      padding: "16px 18px", flex: 1, minWidth: 150,
    }}>
      <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>{label}</div>
      <div style={{ fontFamily: mono, fontSize: 26, fontWeight: 500, color: accent || C.text, marginTop: 8 }}>{value}</div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{sub}</div>
    </div>
  );
}

export default function Collections() {
  const [view, setView] = useState("data"); // data | loading | empty | error
  const [drawer, setDrawer] = useState(null); // selected account for nudge
  const [query, setQuery] = useState("");

  const rows = useMemo(
    () => ACCOUNTS.filter(a => a.name.toLowerCase().includes(query.toLowerCase()))
                  .sort((a, b) => b.dpd - a.dpd),
    [query]
  );

  const kpis = useMemo(() => {
    const total = ACCOUNTS.reduce((s, a) => s + a.out, 0);
    const atRisk = ACCOUNTS.filter(a => a.band === "high" || a.band === "critical").length;
    const samadhaan = ACCOUNTS.filter(a => a.msme && a.dpd >= 45).length;
    return { total, atRisk, samadhaan };
  }, []);

  return (
    <div style={{ display: "flex", minHeight: 640, background: C.bg, fontFamily: sans, color: C.text }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap');
        .rrow:hover { background:${C.bg} !important; }
        .nav:hover { background:#ffffff14; }
      `}</style>

      {/* Sidebar */}
      <aside style={{ width: 224, background: "#0E1118", color: "#E7E9EE", padding: "20px 12px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 8px 18px" }}>
          <div style={{ width: 26, height: 26, borderRadius: 8, background: C.ai, display: "grid", placeItems: "center", fontFamily: mono, fontWeight: 600 }}>र</div>
          <div style={{ fontWeight: 600, letterSpacing: 0.3 }}>Radha</div>
        </div>
        {MODULES.map(m => {
          const Icon = m.icon;
          return (
            <div key={m.code} className="nav" style={{
              display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 8,
              marginBottom: 2, cursor: "pointer",
              background: m.active ? C.primary : "transparent",
              color: m.active ? "#fff" : "#AEB4C0",
            }}>
              <Icon size={15} />
              <span style={{ fontSize: 12.5 }}>{m.name}</span>
              <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 8, opacity: 0.6 }}>{m.code}</span>
            </div>
          );
        })}
        <div style={{ marginTop: 18, padding: "0 8px", fontSize: 10, color: "#5A6172" }}>Bharat Industries Ltd</div>
      </aside>

      {/* Main */}
      <main style={{ flex: 1, padding: "22px 26px", overflow: "auto" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontFamily: mono, fontSize: 10, color: C.muted, letterSpacing: 0.6 }}>M1 · COLLECTIONS COPILOT</div>
            <h1 style={{ fontSize: 22, fontWeight: 600, margin: "4px 0 0" }}>Collections & Revenue Intelligence</h1>
          </div>
          {/* State previewer (demo aid) */}
          <div style={{ display: "flex", gap: 4, background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 10, padding: 4 }}>
            {["data", "loading", "empty", "error"].map(s => (
              <button key={s} onClick={() => setView(s)} style={{
                fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5,
                border: "none", borderRadius: 7, padding: "5px 9px", cursor: "pointer",
                background: view === s ? C.text : "transparent", color: view === s ? "#fff" : C.muted,
              }}>{s}</button>
            ))}
          </div>
        </div>

        {/* KPI cards */}
        <div style={{ display: "flex", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
          <KPICard label="Total Outstanding" value={formatINR(kpis.total)} sub="across 8 accounts" />
          <KPICard label="Accounts at Risk" value={kpis.atRisk} sub="high / critical band" accent={C.danger} />
          <KPICard label="Samadhaan Eligible" value={kpis.samadhaan} sub="MSME · 45+ DPD" accent={C.warning} />
          <KPICard label="Recovery Rate" value="78%" sub="+4 pts vs last month" accent={C.success} />
        </div>

        <div style={{ display: "flex", gap: 16, marginTop: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
          {/* Table + chart */}
          <div style={{ flex: "1 1 540px", minWidth: 320 }}>
            {/* EF-CORE AI panel */}
            <div style={{ background: C.aiBg, border: `0.5px solid ${C.ai}33`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Sparkles size={14} color={C.ai} />
                <span style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.8, color: C.ai }}>EF-CORE · AI INSIGHT</span>
                <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 9, color: C.ai }}>confidence 0.88</span>
              </div>
              <p style={{ margin: "10px 0 0", fontSize: 13.5, lineHeight: 1.5 }}>
                <b>Ganga Engineering Works</b> is MSME and 96 days past due — now <b>Samadhaan-eligible</b>.
                Recommended: prepare the Samadhaan pre-notice and send a courtesy WhatsApp reminder in Gujarati.
                Two MSME accounts have crossed the 45-day threshold this week.
              </p>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button onClick={() => setDrawer(ACCOUNTS[4])} style={{
                  background: C.ai, color: "#fff", border: "none", borderRadius: 8,
                  padding: "7px 13px", fontSize: 12, fontWeight: 500, cursor: "pointer",
                }}>Review & approve</button>
                <button style={{ background: "transparent", color: C.ai, border: `0.5px solid ${C.ai}55`, borderRadius: 8, padding: "7px 13px", fontSize: 12, cursor: "pointer" }}>Dismiss</button>
              </div>
            </div>

            {/* Table card */}
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", borderBottom: `0.5px solid ${C.border}` }}>
                <Search size={14} color={C.muted} />
                <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Filter accounts…"
                  style={{ border: "none", outline: "none", fontSize: 13, flex: 1, background: "transparent", color: C.text }} />
                <span style={{ fontFamily: mono, fontSize: 10, color: C.muted }}>{rows.length} accounts</span>
              </div>

              {view === "loading" && <SkeletonRows />}
              {view === "error" && <ErrorState />}
              {view === "empty" && <EmptyState />}
              {view === "data" && (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["Debtor", "Outstanding", "DPD", "Risk", "Recovery", "Agent"].map(h => (
                        <th key={h} style={{
                          textAlign: h === "Outstanding" || h === "DPD" || h === "Recovery" ? "right" : "left",
                          fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6,
                          color: C.muted, fontWeight: 500, padding: "10px 14px", borderBottom: `0.5px solid ${C.border}`,
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((a, i) => (
                      <tr key={i} className="rrow" onClick={() => setDrawer(a)}
                        style={{ cursor: "pointer", borderBottom: `0.5px solid ${C.border}` }}>
                        <td style={{ padding: "11px 14px" }}>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>{a.name}</div>
                          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                            <span style={{ fontSize: 10, color: C.muted }}>{a.state}</span>
                            {a.msme && a.dpd >= 45 && <Badge color={C.warning} bg="#FBF2E0">Samadhaan</Badge>}
                            {a.msme && a.dpd < 45 && <Badge color={C.muted} bg={C.bg}>MSME</Badge>}
                          </div>
                        </td>
                        <td style={{ padding: "11px 14px", textAlign: "right", fontFamily: mono, fontSize: 13 }}>{formatINR(a.out)}</td>
                        <td style={{ padding: "11px 14px", textAlign: "right", fontFamily: mono, fontSize: 13, color: a.dpd >= 60 ? C.danger : a.dpd >= 30 ? C.warning : C.text }}>{a.dpd}d</td>
                        <td style={{ padding: "11px 14px" }}>
                          <Badge color={BAND[a.band]} bg={`${BAND[a.band]}14`}>{a.band}</Badge>
                        </td>
                        <td style={{ padding: "11px 14px", textAlign: "right", fontFamily: mono, fontSize: 13, color: C.muted }}>{Math.round(a.rec * 100)}%</td>
                        <td style={{ padding: "11px 14px", fontSize: 12, color: C.muted }}>{a.agent}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Recovery trend chart */}
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: "14px 14px 6px", marginTop: 16 }}>
              <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted, marginBottom: 8 }}>Recovery rate trend</div>
              <ResponsiveContainer width="100%" height={150}>
                <AreaChart data={RECOVERY_TREND} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={C.primary} stopOpacity={0.25} />
                      <stop offset="100%" stopColor={C.primary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="2 4" stroke={C.border} vertical={false} />
                  <XAxis dataKey="m" tick={{ fontSize: 10, fill: C.muted, fontFamily: mono }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: C.muted, fontFamily: mono }} axisLine={false} tickLine={false} domain={[50, 85]} />
                  <Tooltip formatter={(v) => `${v}%`} contentStyle={{ borderRadius: 8, border: `0.5px solid ${C.border}`, fontSize: 12 }} />
                  <Area type="monotone" dataKey="rate" stroke={C.primary} strokeWidth={2} fill="url(#g)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Right rail: compliance posture */}
          <div style={{ flex: "0 0 230px", minWidth: 200 }}>
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
              <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Compliance posture</div>
              {[
                { i: ShieldCheck, c: C.success, t: "RBI contact window", s: "08:00–19:00 IST · enforced" },
                { i: ShieldCheck, c: C.success, t: "Daily contact cap", s: "max 3 / account / day" },
                { i: ShieldCheck, c: C.success, t: "Prohibited language", s: "screened pre-dispatch" },
                { i: AlertTriangle, c: C.warning, t: "MSME Samadhaan", s: "2 accounts eligible" },
                { i: ShieldCheck, c: C.success, t: "DPDP PAN masking", s: "XXXXX####X in all logs" },
              ].map((r, i) => {
                const Ic = r.i;
                return (
                  <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start", marginTop: 13 }}>
                    <Ic size={15} color={r.c} style={{ marginTop: 1, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 12.5, fontWeight: 500 }}>{r.t}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{r.s}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </main>

      {/* Action drawer */}
      {drawer && <NudgeDrawer account={drawer} onClose={() => setDrawer(null)} />}
    </div>
  );
}

function SkeletonRows() {
  return (
    <div style={{ padding: 14 }}>
      {[...Array(5)].map((_, i) => (
        <div key={i} style={{ height: 18, background: C.border, borderRadius: 6, marginBottom: 14, opacity: 0.6, animation: "pulse 1.4s ease-in-out infinite" }} />
      ))}
      <style>{`@keyframes pulse{0%,100%{opacity:.35}50%{opacity:.7}}`}</style>
    </div>
  );
}
function ErrorState() {
  return (
    <div style={{ padding: 40, textAlign: "center" }}>
      <AlertTriangle size={26} color={C.danger} />
      <div style={{ marginTop: 10, fontWeight: 500 }}>Couldn’t load accounts</div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>The collections service did not respond.</div>
      <button style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 6, background: C.primary, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer" }}>
        <RefreshCw size={13} /> Retry
      </button>
    </div>
  );
}
function EmptyState() {
  return (
    <div style={{ padding: 44, textAlign: "center" }}>
      <Inbox size={26} color={C.muted} />
      <div style={{ marginTop: 10, fontWeight: 500 }}>No accounts under collection</div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Import overdue invoices to begin.</div>
      <button style={{ marginTop: 14, background: C.primary, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer" }}>
        Import accounts
      </button>
    </div>
  );
}

function NudgeDrawer({ account, onClose }) {
  const [channel, setChannel] = useState("whatsapp");
  const [body, setBody] = useState(
    account.state === "Gujarat"
      ? "Namaste, this is a courtesy reminder from Bharat Industries regarding invoice dues. Kindly arrange payment or reply to discuss a schedule."
      : "Gentle reminder: your invoice balance is past due. Please arrange payment at your convenience, or reply to discuss a schedule."
  );
  const PROHIBITED = ["threaten", "sue", "police", "court", "jail", "seize", "attach", "ruin"];
  const found = PROHIBITED.filter(w => new RegExp(`\\b${w}\\b`, "i").test(body));
  const istHour = new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata", hour: "2-digit", hour12: false });
  const inWindow = +istHour >= 8 && +istHour < 19;
  const blocked = found.length > 0 || !inWindow;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "#08090C55", display: "flex", justifyContent: "flex-end", zIndex: 50 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 380, background: C.surface, height: "100%", padding: 22, overflow: "auto", boxShadow: "-12px 0 40px #0003" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontFamily: mono, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Send reminder</div>
          <X size={18} color={C.muted} style={{ cursor: "pointer" }} onClick={onClose} />
        </div>
        <h3 style={{ margin: "8px 0 2px", fontSize: 17 }}>{account.name}</h3>
        <div style={{ fontFamily: mono, fontSize: 13, color: C.muted }}>{formatINR(account.out)} · {account.dpd}d overdue</div>

        <div style={{ marginTop: 18, fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Channel</div>
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          {["whatsapp", "email", "sms"].map(c => (
            <button key={c} onClick={() => setChannel(c)} style={{
              flex: 1, fontSize: 12, padding: "8px 0", borderRadius: 8, cursor: "pointer",
              border: `0.5px solid ${channel === c ? C.primary : C.border}`,
              background: channel === c ? `${C.primary}10` : C.surface,
              color: channel === c ? C.primary : C.text, textTransform: "capitalize",
            }}>{c}</button>
          ))}
        </div>

        <div style={{ marginTop: 16, fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Message</div>
        <textarea value={body} onChange={e => setBody(e.target.value)} rows={5} style={{
          width: "100%", marginTop: 8, padding: 10, fontSize: 13, lineHeight: 1.5,
          border: `0.5px solid ${found.length ? C.danger : C.border}`, borderRadius: 8, resize: "vertical",
          fontFamily: sans, color: C.text, boxSizing: "border-box",
        }} />

        {/* RBI compliance status */}
        <div style={{ marginTop: 12, background: blocked ? "#FBE9E7" : "#E9F7F1", border: `0.5px solid ${blocked ? C.danger : C.success}33`, borderRadius: 8, padding: 11 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            {blocked ? <AlertTriangle size={14} color={C.danger} /> : <ShieldCheck size={14} color={C.success} />}
            <span style={{ fontSize: 12, fontWeight: 500, color: blocked ? C.danger : C.success }}>
              {blocked ? "Blocked by RBI guard" : "Passes RBI checks"}
            </span>
          </div>
          <ul style={{ margin: "8px 0 0 18px", padding: 0, fontSize: 11.5, color: C.muted }}>
            <li>Contact window 08:00–19:00 IST — {inWindow ? "within window" : `outside (now ${istHour}:00 IST)`}</li>
            <li>Prohibited language — {found.length ? `found: ${found.join(", ")}` : "none detected"}</li>
            <li>Daily cap — under 3 contacts today</li>
          </ul>
        </div>

        <button disabled={blocked} style={{
          width: "100%", marginTop: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          background: blocked ? C.border : C.primary, color: blocked ? C.muted : "#fff",
          border: "none", borderRadius: 9, padding: "11px 0", fontSize: 13, fontWeight: 500,
          cursor: blocked ? "not-allowed" : "pointer",
        }}>
          <Send size={14} /> {blocked ? "Cannot send" : "Send reminder"}
        </button>
      </div>
    </div>
  );
}
